import type { Briefing } from '@acasa/shared'
import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { PageShell } from '@/components/page-shell'
import { CreateBriefingDialog, estateChoiceOf } from '@/features/briefings/create-dialog'
import { briefingName, briefingStand, NOT_CONFIGURED } from '@/features/briefings/format'
import { LimitNotice } from '@/features/briefings/limit-notice'
import { BriefingStatusChip } from '@/features/briefings/status-chip'
import { useBriefings } from '@/features/briefings/use-briefings'

function BriefingRow({ briefing }: { briefing: Briefing }) {
  return (
    <li className="border-line bg-surface border">
      <Link
        to={`/briefings/${briefing.id}`}
        className="hover:bg-canvas block p-3 transition-colors sm:p-4"
      >
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-ink tnum font-mono text-sm font-medium">
            {briefing.estate.externalNumber ?? '—'}
          </span>
          <BriefingStatusChip status={briefing.status} />
          {/* Only the CURRENT one: the field in onOffice holds a single url. */}
          {briefing.onOfficeLink.current && (
            <span className="text-faint text-[10px] tracking-wider uppercase">in onOffice</span>
          )}
        </div>
        <p className="text-muted mt-1 truncate text-xs">{briefingName(briefing)}</p>
        <p className="text-faint mt-1 flex flex-wrap gap-x-3 text-[11px]">
          <span>Stand {briefingStand(briefing)}</span>
          {briefing.createdBy && <span>{briefing.createdBy.name}</span>}
          <span>
            {briefing.sourceFiles.length}{' '}
            {briefing.sourceFiles.length === 1 ? 'Dokument' : 'Dokumente'}
          </span>
        </p>
      </Link>
    </li>
  )
}

export function BriefingsPage() {
  const [params, setParams] = useSearchParams()
  const estateParam = Number(params.get('estate'))
  const estateId = Number.isInteger(estateParam) && estateParam > 0 ? estateParam : null

  const { data, isPending, isError, error } = useBriefings(estateId)
  const [creating, setCreating] = useState(false)
  const [term, setTerm] = useState('')

  const briefings = data?.briefings ?? []
  const visible = useMemo(() => {
    const needle = term.trim().toLowerCase()
    if (!needle) return briefings
    return briefings.filter((briefing) =>
      [briefing.estate.externalNumber, briefing.estate.title, briefingName(briefing)]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(needle),
    )
  }, [briefings, term])

  const configured = data?.configured ?? true
  const limitReached = data?.limitReached ?? false
  const filteredEstate = estateId !== null ? briefings[0] : undefined
  const prefill = filteredEstate ? estateChoiceOf(filteredEstate) : null

  return (
    <PageShell>
      <header className="border-line mb-6 flex flex-wrap items-end justify-between gap-3 border-b pb-5">
        <div className="min-w-0 flex-1">
          <span className="bg-brand mb-3 block h-[3px] w-8" aria-hidden />
          <h1 className="font-display text-ink text-[26px] leading-tight">Objekt-Briefings</h1>
          <p className="text-muted mt-2 text-sm leading-relaxed">
            Auswertung der Eigentümerversammlungs-Protokolle und der Beschlusssammlung eines Objekts
            — aus den PDF- und Word-Dateien in onOffice.
          </p>
        </div>
        <button
          type="button"
          disabled={!configured || limitReached}
          onClick={() => setCreating(true)}
          className="bg-brand text-surface hover:bg-brand-strong shrink-0 px-4 py-2.5 text-xs font-medium transition-colors disabled:opacity-40"
        >
          Neues Briefing
        </button>
      </header>

      {!configured && (
        <p className="border-line bg-raised text-muted mb-5 border px-3 py-2 text-xs">
          {NOT_CONFIGURED} Bestehende Briefings bleiben lesbar.
        </p>
      )}
      {configured && limitReached && (
        <div className="mb-5">
          <LimitNotice />
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Objekt-Nr. oder Adresse"
          aria-label="Briefings nach Objekt filtern"
          className="border-line focus:border-brand bg-surface text-ink placeholder:text-faint w-full max-w-xs border px-3 py-2 text-xs outline-none"
        />
        {estateId !== null && (
          <span className="border-line bg-sand-soft text-ink inline-flex items-center gap-2 border px-2.5 py-1.5 text-[11px]">
            Objekt {filteredEstate?.estate.externalNumber ?? `#${estateId}`}
            <button
              type="button"
              onClick={() => {
                params.delete('estate')
                setParams(params)
              }}
              aria-label="Objektfilter aufheben"
              className="text-muted hover:text-ink"
            >
              ×
            </button>
          </span>
        )}
      </div>

      {isPending && <p className="text-muted py-12 text-center text-sm">Wird geladen …</p>}

      {isError && (
        <p className="border-danger/30 bg-danger/5 text-danger border px-3 py-2 text-xs">
          {error instanceof Error ? error.message : 'Unbekannter Fehler'}
        </p>
      )}

      {data && briefings.length === 0 && (
        <div className="border-line text-muted border border-dashed px-4 py-12 text-center text-sm">
          {estateId !== null
            ? 'Für dieses Objekt gibt es noch kein Briefing.'
            : 'Noch kein Briefing erstellt.'}
        </div>
      )}

      {data && briefings.length > 0 && visible.length === 0 && (
        <p className="text-muted py-8 text-center text-sm">Kein Briefing passt zum Filter.</p>
      )}

      {visible.length > 0 && (
        <ul className="space-y-2">
          {visible.map((briefing) => (
            <BriefingRow key={briefing.id} briefing={briefing} />
          ))}
        </ul>
      )}

      {creating && <CreateBriefingDialog estate={prefill} onClose={() => setCreating(false)} />}
    </PageShell>
  )
}
